import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { Accept } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { SlideoutPanelDialog } from "../../components/SlideoutPanel/SlideoutPanelDialog.component";
import Dropzone from "../FileDropzone/Dropzone.component";
import type { FileObject } from "../FileDropzone/typings/FileObject.type";
import {
  clampVisitorConsentDocumentDisplayOrder,
  displayOrderToStoredOrder,
  storedOrderCapToDisplayMax,
  storedOrderToDisplayOrder,
  VISITOR_CONSENT_DOCUMENT_DISPLAY_ORDER_MIN,
  type VisitorConsentDocument,
  type VisitorConsentDocumentUpsertDraft
} from "./visitor-consent-documents.context";
import { getVisitorConsentAttachmentPreviewKind } from "./visitor-consent-attachment-preview.util";
import { VisitorConsentDocumentAttachmentPreview } from "./visitor-consent-document-attachment-preview.component";

const ACCEPT_CONSENT_UPLOADS: Accept = {
  "application/pdf": [".pdf"],
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/webp": [".webp"],
  "image/gif": [".gif"]
};

type P = {
  open: boolean;
  mode: "add" | "edit";
  document: VisitorConsentDocument | null;
  nextOrderSuggestion: number;
  /** Inclusive upper bound for stored order (add: max existing + 1; edit: max existing). */
  orderMax: number;
  onClose: () => void;
  onAdd: (draft: VisitorConsentDocumentUpsertDraft) => void;
  onEdit: (id: string, draft: VisitorConsentDocumentUpsertDraft) => void;
};

const revokeDropzonePreview = (files: FileObject[]) => {
  files.forEach(fo => {
    if (fo.file.preview.startsWith("blob:")) {
      URL.revokeObjectURL(fo.file.preview);
    }
  });
};

export const VisitorConsentDocumentsFormDialog: React.FC<P> = ({
  open,
  mode,
  document,
  nextOrderSuggestion,
  orderMax,
  onClose,
  onAdd,
  onEdit
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [body, setBody] = useState("");
  const [displayOrder, setDisplayOrder] = useState(VISITOR_CONSENT_DOCUMENT_DISPLAY_ORDER_MIN);
  const [attachedFiles, setAttachedFiles] = useState<FileObject[]>([]);
  const [removeExistingAttachment, setRemoveExistingAttachment] = useState(false);

  const displayOrderMax = storedOrderCapToDisplayMax(orderMax);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (mode === "edit" && document) {
      setTitle(document.title);
      setDescription(document.description);
      setBody(document.body);
      setDisplayOrder(storedOrderToDisplayOrder(document.order, displayOrderMax));
      setAttachedFiles([]);
      setRemoveExistingAttachment(false);
      return;
    }
    setTitle("");
    setDescription("");
    setBody("");
    setDisplayOrder(storedOrderToDisplayOrder(nextOrderSuggestion, displayOrderMax));
    setAttachedFiles([]);
    setRemoveExistingAttachment(false);
  }, [document, displayOrderMax, mode, nextOrderSuggestion, open, orderMax]);

  const detachAndReset = () => {
    revokeDropzonePreview(attachedFiles);
    setAttachedFiles([]);
  };

  const handleClose = () => {
    detachAndReset();
    onClose();
  };

  const newFileChosen = attachedFiles[0]?.file ?? null;
  const hasExistingAttachmentKept =
    mode === "edit" && Boolean(document?.fileViewerUrl) && !removeExistingAttachment;

  const showExistingFilePreview =
    mode === "edit" &&
    Boolean(document?.fileViewerUrl && document.fileName) &&
    !removeExistingAttachment &&
    attachedFiles.length === 0;

  const existingAttachmentPreview =
    showExistingFilePreview && document?.fileViewerUrl && document.fileName
      ? {
          url: document.fileViewerUrl,
          fileName: document.fileName,
          kind: getVisitorConsentAttachmentPreviewKind(document.fileName)
        }
      : null;

  const hasReadableContent =
    body.trim().length > 0 || Boolean(newFileChosen) || Boolean(hasExistingAttachmentKept);

  const canSave =
    title.trim().length > 0 &&
    hasReadableContent &&
    Number.isFinite(displayOrder) &&
    Number.isInteger(displayOrder) &&
    displayOrder >= VISITOR_CONSENT_DOCUMENT_DISPLAY_ORDER_MIN &&
    displayOrder <= displayOrderMax;

  const handleSave = () => {
    const draftFile = newFileChosen;
    const normalizedOrder = displayOrderToStoredOrder(displayOrder, orderMax);

    const common: VisitorConsentDocumentUpsertDraft = {
      title,
      description,
      body,
      order: normalizedOrder,
      file: draftFile,
      dropExistingAttachment:
        mode === "edit" &&
        Boolean(document?.fileViewerUrl) &&
        removeExistingAttachment &&
        !draftFile
    };

    detachAndReset();

    if (mode === "edit" && document) {
      onEdit(document.id, common);
    } else {
      onAdd(common);
    }
    onClose();
  };

  const dialogTitle =
    mode === "add"
      ? t("_visitorConsentDocuments_dialogAddTitle")
      : t("_visitorConsentDocuments_dialogEditTitle");

  return (
    <Box data-testid="visitor-consent-doc-form-dialog">
      <SlideoutPanelDialog
        isOpen={open}
        dialogTitle={dialogTitle}
        width="40vw"
        showCloseButton
        onClose={handleClose}
      >
        <Stack spacing={5} sx={{ mt: 1 }}>
          <TextField
            label={t("_visitorConsentDocuments_fieldTitle")}
            value={title}
            onChange={event => setTitle(event.target.value)}
            required
            fullWidth
            slotProps={{
              htmlInput: { "data-testid": "visitor-consent-doc-field-title" }
            }}
          />
          <TextField
            label={t("_visitorConsentDocuments_fieldDescription")}
            value={description}
            onChange={event => setDescription(event.target.value)}
            multiline
            minRows={2}
            fullWidth
            slotProps={{
              htmlInput: { "data-testid": "visitor-consent-doc-field-description" }
            }}
          />
          <TextField
            label={t("_visitorConsentDocuments_fieldBody")}
            value={body}
            onChange={event => setBody(event.target.value)}
            multiline
            minRows={4}
            fullWidth
            helperText={t("_visitorConsentDocuments_fieldBodyHelper")}
            slotProps={{
              htmlInput: { "data-testid": "visitor-consent-doc-field-body" }
            }}
          />
          <TextField
            label={t("_visitorConsentDocuments_fieldOrder")}
            type="number"
            value={displayOrder}
            onChange={event => {
              const raw = event.target.value;
              if (raw === "") {
                setDisplayOrder(VISITOR_CONSENT_DOCUMENT_DISPLAY_ORDER_MIN);
                return;
              }
              const parsed = Number(raw);
              if (!Number.isFinite(parsed)) return;
              setDisplayOrder(clampVisitorConsentDocumentDisplayOrder(parsed, displayOrderMax));
            }}
            required
            fullWidth
            helperText={t("_visitorConsentDocuments_fieldOrderHelper", {
              min: VISITOR_CONSENT_DOCUMENT_DISPLAY_ORDER_MIN,
              max: displayOrderMax
            })}
            slotProps={{
              htmlInput: {
                "data-testid": "visitor-consent-doc-field-order",
                min: VISITOR_CONSENT_DOCUMENT_DISPLAY_ORDER_MIN,
                max: displayOrderMax,
                step: 1
              }
            }}
          />

          {/* Existing file preview */}
          {mode === "edit" && document?.fileName && (
            <Stack spacing={1} data-testid="visitor-consent-doc-existing-file-preview">
              {existingAttachmentPreview && (
                <Typography variant="subtitle2" color="text.secondary">
                  {t("_visitorConsentDocuments_existingFilePreview")}
                </Typography>
              )}
              <Stack spacing={0}>
                {existingAttachmentPreview && (
                  <VisitorConsentDocumentAttachmentPreview
                    fileViewerUrl={existingAttachmentPreview.url}
                    fileName={existingAttachmentPreview.fileName}
                    openLinkLabel={t("_visitorConsent_viewAttachedDocument")}
                    pdfPreviewUnavailableMessage={t("_visitorConsent_attachmentPreviewUnavailable")}
                    pdfPreviewLoadingMessage={t("_visitorConsent_attachmentPreviewLoading")}
                    testIds={{
                      root: "visitor-consent-doc-existing-file-preview-content",
                      image: "visitor-consent-doc-existing-file-preview-image",
                      pdf: "visitor-consent-doc-existing-file-preview-pdf",
                      link: "visitor-consent-doc-existing-file-preview-link"
                    }}
                  />
                )}
                <FormControlLabel
                  sx={{
                    alignItems: "center",
                    width: "100%",
                    mr: 0,
                    "& .MuiFormControlLabel-label": {
                      overflowWrap: "anywhere",
                      whiteSpace: "normal"
                    }
                  }}
                  control={
                    <Checkbox
                      checked={removeExistingAttachment}
                      onChange={event => setRemoveExistingAttachment(event.target.checked)}
                      data-testid="visitor-consent-doc-remove-attachment"
                    />
                  }
                  label={t("_visitorConsentDocuments_removeAttachment", {
                    file: document.fileName
                  })}
                />
              </Stack>
            </Stack>
          )}
          <Dropzone
            data-testid="visitor-consent-doc-dropzone"
            files={attachedFiles}
            dropzoneDesc={t("_visitorConsentDocuments_dropzoneDesc")}
            acceptedFileTypes={ACCEPT_CONSENT_UPLOADS}
            maxFiles={1}
            maxFileSize={10 * 1024 * 1024}
            containerHeight="140px"
            onAdd={list => setAttachedFiles(list)}
            onDelete={() => setAttachedFiles([])}
          />
        </Stack>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
          <Button onClick={handleClose} color="secondary">
            {t("_visitorConsentDocuments_cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSave()}
            disabled={!canSave}
            data-testid="visitor-consent-doc-save"
          >
            {t("_visitorConsentDocuments_save")}
          </Button>
        </Box>
      </SlideoutPanelDialog>
    </Box>
  );
};
